import { PdfGen, PdfImage } from '../pdf';
import type { Book, BookOptions } from './types';
import { parseBook } from './parser';
import type { ParsedBook } from './parser';
import { renderBook } from './renderer';
import type { RenderResult } from './renderer';
import type { NotesCollection } from './notes';
import { initializeModules } from '../../modules';
import { setPreloadedImages } from '../../modules/image';
import { IImageLoader, IFileAccess } from '@educraft/base';

export type { Book, Chapter, Section, BookOptions } from './types';
export type { ContentElement, InlineElement } from './types';
export type { ParsedBook } from './parser';
export type { RenderResult } from './renderer';
export type { NotesCollection, Note, NoteType } from './notes';
export { Hyphenator, hyphenate, createHyphenator, addHyphenationPoints } from './hyphenation';
export { getHyphenator, loadPatterns, createLanguageHyphenator } from './hyphenator';
export {
  PatternCache,
  patternCache,
  getCachedPatterns,
  setCachedPatterns,
  hasCachedPatterns,
  clearPatternCache,
  getPatternCacheStats,
} from './patternCache';

export interface RenderBookOptions extends BookOptions {
  title?: string;
}

async function preloadImages(
  pdf: PdfGen,
  markdown: string,
  imageLoader?: IImageLoader,
  fileAccess?: IFileAccess
): Promise<void> {
  const srcSet = new Set<string>();
  const imgRegex = /<img\b[^>]*\bsrc=(?:"([^"]*)"|'([^']*)')/g;
  let match: RegExpExecArray | null;
  while ((match = imgRegex.exec(markdown)) !== null) {
    const src = match[1] || match[2];
    if (src) srcSet.add(src);
  }

  if (srcSet.size === 0 || (!imageLoader && !fileAccess)) return;

  const map = new Map<string, PdfImage>();

  for (const src of srcSet) {
    let bytes: Uint8Array | null = null;
    if (fileAccess && fileAccess.exists(src)) {
      bytes = fileAccess.getBytes(src);
    } else if (imageLoader) {
      bytes = await imageLoader.loadImage(src);
    }

    if (bytes) {
      const ext = src.split('.').pop()?.toLowerCase() || 'png';
      const type: 'png' | 'jpeg' = ext === 'jpg' || ext === 'jpeg' ? 'jpeg' : 'png';
      const raw = await pdf.embedImage(src, bytes, type);
      map.set(src, new PdfImage(raw, raw.width, raw.height));
    } else {
      console.warn(`Image not found: ${src}`);
    }
  }

  setPreloadedImages(map);
}

export async function render(
  pdf: PdfGen,
  markdown: string,
  options: RenderBookOptions = {}
): Promise<RenderResult> {
  initializeModules();

  const { title, language, fontSize, lineHeight, margin, columns, defaultAlignment, imageLoader, fileAccess } =
    options;

  if (imageLoader || fileAccess) {
    await preloadImages(pdf, markdown, imageLoader, fileAccess);
  }

  const parsed = parseBook(markdown, title);

  const bookOptions: BookOptions = {
    language,
    fontSize,
    lineHeight,
    margin,
    columns,
    defaultAlignment,
    imageLoader,
    fileAccess,
  };

  return renderBook(pdf, parsed.book, parsed.notes, bookOptions);
}

export function parse(markdown: string, title?: string): ParsedBook {
  initializeModules();
  return parseBook(markdown, title);
}

export function getNotes(markdown: string): NotesCollection {
  initializeModules();
  const parsed = parseBook(markdown);
  return parsed.notes;
}
